import { useEffect, useState, type FormEvent } from 'react';
import type { Squad } from '@fantasy/types';
import {
  ApiRequestError,
  api,
  type LivePlayerPoints,
  type ManagerHistoryPoint,
  type ManagerLivePoints,
  type OwnSquadHistoryPoint,
  type WatchedManager,
} from '../api.ts';
import { estimateSquadPoints } from '../lib/estimateSquadPoints.ts';
import { GameweekChart, type GameweekSeries } from './GameweekChart.tsx';
import { RivalDifferentials } from './RivalDifferentials.tsx';

/** Fixed categorical order — validated via the dataviz skill's palette
 * validator (light mode, CVD-adjacent-pair check) for exactly this
 * assign-in-order use. Never cycled: a 9th watched manager goes uncoloured
 * and unplotted rather than reusing a hue (see `MAX_PLOTTED_MANAGERS`). */
const CHART_PALETTE = [
  '#2a78d6', // blue
  '#eb6834', // orange
  '#1baf7a', // aqua
  '#eda100', // yellow
  '#e87ba4', // magenta
  '#008300', // green
  '#4a3aa7', // violet
  '#e34948', // red
];
/** Slot 0 is reserved for "Your squad" whenever a squad is loaded — your own
 * points are always plotted, so they always get the same colour rather than
 * competing with watched managers for a slot. Watched managers start from
 * slot 1 in that case; with no squad loaded there's no "Your squad" line to
 * reserve a slot for, so managers get the full palette. */
function paletteForManagers(hasSquad: boolean): string[] {
  return hasSquad ? CHART_PALETTE.slice(1) : CHART_PALETTE;
}
const OWN_SQUAD_COLOR = CHART_PALETTE[0] ?? '#2a78d6';

interface TrackerProps {
  squad: Squad | null;
}

/**
 * Its own page (T-048) — previously a collapsed accordion stacked below the
 * squad desk, competing for space with everything else there. Nothing here
 * is squad-specific in the way the squad desk's own sections are: any
 * manager, anywhere, can be tracked and compared by their public FPL entry
 * id, alongside the signed-in user's own squad when one is loaded. With a
 * whole page to itself there's nothing to gain from starting collapsed —
 * everything (the tracked list, everyone's live points, and the gameweek
 * chart) loads automatically on mount, unrolled from the beginning rather
 * than waiting on a click to open it first.
 */
export function Tracker({ squad }: TrackerProps) {
  const [managers, setManagers] = useState<WatchedManager[]>([]);
  const [history, setHistory] = useState<ManagerHistoryPoint[]>([]);
  const [ownSquadHistory, setOwnSquadHistory] = useState<OwnSquadHistoryPoint[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [entryIdInput, setEntryIdInput] = useState('');
  const [addingManager, setAddingManager] = useState(false);

  const [managerLivePoints, setManagerLivePoints] = useState<Map<number, ManagerLivePoints> | null>(null);
  const [squadLivePoints, setSquadLivePoints] = useState<Map<number, LivePlayerPoints> | null>(null);

  /** At most one rival's differential comparison expanded at a time — the
   * comparison is per-squad, not per-manager, so showing several side by
   * side would just repeat "your squad" for each. */
  const [expandedEntryId, setExpandedEntryId] = useState<number | null>(null);

  /** Everything this page shows, fetched together as one action: the
   * tracked-managers list, live points for each of them (and the signed-in
   * user's own squad, if one is loaded), and the history that feeds the
   * chart. Runs automatically on mount — see the effect below — and again
   * on demand via the "Refresh" button, since gameweek points keep changing
   * while a match is still being played. Once this gameweek's own-squad
   * estimate is known it's persisted server-side too, so "your points"
   * keeps building a real history the same way a tracked manager's does.
   * History is re-fetched only after that write (and the live-points fetch
   * itself) land, never in parallel with them, since it's written
   * server-side as a side effect of the live-points call and would
   * otherwise risk reading stale data. */
  async function refresh() {
    setRefreshing(true);
    setError(null);
    try {
      const [{ managers: fetchedManagers }, { managers: managerPoints }, squadPoints] = await Promise.all([
        api.getWatchedManagers(),
        api.getWatchedManagersLivePoints(),
        squad ? api.getLivePoints(squad.id) : Promise.resolve(null),
      ]);

      const nextSquadLivePoints = squadPoints ? new Map(squadPoints.points.map((p) => [p.fplId, p])) : null;
      const nextSquadEstimate = squad ? estimateSquadPoints(squad, nextSquadLivePoints) : null;
      if (nextSquadEstimate !== null) {
        await api.recordOwnSquadSnapshot(nextSquadEstimate);
      }

      const { history: fetchedHistory, ownSquadHistory: fetchedOwnSquad } = await api.getWatchedManagersHistory();

      setManagers(fetchedManagers);
      setManagerLivePoints(new Map(managerPoints.map((m) => [m.entryId, m])));
      setHistory(fetchedHistory);
      setOwnSquadHistory(fetchedOwnSquad);
      setSquadLivePoints(nextSquadLivePoints);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not load your tracker.');
    } finally {
      setRefreshing(false);
      setLoaded(true);
    }
  }

  // Unrolled from the beginning — no click-to-open needed on a page with
  // nothing else competing for space. Keyed on `squad?.id` rather than run
  // once unconditionally: `useSquad`'s own mount-time restore can still be
  // in flight when this page mounts, so a squad that resolves a moment
  // later still triggers its own refresh rather than being permanently
  // missed until a manual "Refresh" click.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    void refresh();
  }, [squad?.id]);

  /** No search/typeahead — entry ids come from a URL the manager already has
   * open, so this is a plain number field. A full `refresh()` afterward
   * (rather than just re-fetching the identity list) means the newly added
   * manager's live points and history show up immediately too, not only
   * after a separate manual refresh. */
  async function onAddManager(e: FormEvent) {
    e.preventDefault();
    const entryId = Number(entryIdInput);
    if (!Number.isInteger(entryId) || entryId <= 0) return;

    setError(null);
    setAddingManager(true);
    try {
      await api.addWatchedManager(entryId);
      setEntryIdInput('');
      await refresh();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not add that manager.');
    } finally {
      setAddingManager(false);
    }
  }

  async function onRemoveManager(entryId: number) {
    setError(null);
    try {
      await api.removeWatchedManager(entryId);
      setManagers((prev) => prev.filter((m) => m.entryId !== entryId));
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not remove that manager.');
    }
  }

  const squadEstimate = squad ? estimateSquadPoints(squad, squadLivePoints) : null;

  // Colour assigned by list position, fixed order, never cycled — a 9th+
  // tracked manager still appears in the list and its own points, just
  // without a chart line (see the dataviz skill's "fold into Other" rule).
  const managerPalette = paletteForManagers(squad !== null);
  const plottedManagers = managers.slice(0, managerPalette.length);
  const overflowCount = managers.length - plottedManagers.length;
  const managerSeries: GameweekSeries[] = plottedManagers.map((manager, index) => ({
    id: manager.entryId,
    label: manager.teamName,
    // `plottedManagers` is capped at managerPalette.length above, so this
    // index is always in range — the fallback only satisfies noUncheckedIndexedAccess.
    color: managerPalette[index] ?? managerPalette[0] ?? '#2a78d6',
    points: history
      .filter((h) => h.entryId === manager.entryId)
      .sort((a, b) => a.gameweek - b.gameweek),
  }));
  // "Your squad" is always included whenever a squad is loaded — its own
  // reserved slot 0, never competing with tracked managers for a colour —
  // so your points are always plotted, not just tracked managers'.
  const ownSquadSeries: GameweekSeries | null = squad
    ? {
        id: -1,
        label: 'Your squad (est.)',
        color: OWN_SQUAD_COLOR,
        points: [...ownSquadHistory].sort((a, b) => a.gameweek - b.gameweek),
      }
    : null;
  const chartSeries: GameweekSeries[] = [...(ownSquadSeries ? [ownSquadSeries] : []), ...managerSeries];
  const colorByEntryId = new Map(managerSeries.map((s) => [s.id, s.color]));

  // Full-page loading placeholder only for the very first fetch, when
  // there's genuinely nothing to show yet — a later manual refresh keeps
  // the existing list/chart visible and just puts the button itself into a
  // busy state, rather than blanking the whole section again.
  const showFullPageLoading = refreshing && !loaded;

  return (
    <section className="panel panel--accent-2" aria-labelledby="tracker-heading">
      <div className="panel-head">
        <h3 id="tracker-heading">Tracker</h3>
        <span className="hint">
          {!loaded
            ? 'loading…'
            : managers.length === 0
              ? 'empty'
              : managers.length === 1
                ? '1 manager'
                : `${managers.length} managers`}
        </span>
      </div>

      {showFullPageLoading ? (
        <p className="hint">Loading your tracker…</p>
      ) : (
        <>
          <form className="row row--flush" onSubmit={onAddManager}>
            <input
              type="number"
              aria-label="FPL entry id"
              placeholder="FPL entry id"
              value={entryIdInput}
              onChange={(e) => setEntryIdInput(e.target.value)}
            />
            <button type="submit" className="ghost small" disabled={addingManager} aria-busy={addingManager}>
              {addingManager ? 'Adding…' : 'Add manager'}
            </button>
          </form>

          {managers.length === 0 && !squad && (
            <p className="hint mt-md">
              No managers tracked yet — paste an FPL entry id above to compare (find one in a manager's
              team URL, e.g. fantasy.premierleague.com/entry/<code>2567551</code>/event/1).
            </p>
          )}

          {(squad || managers.length > 0) && (
            <div className="mt-md">
              {ownSquadSeries && (
                <div className="manager-row">
                  <span className="gameweek-chart-swatch" style={{ background: OWN_SQUAD_COLOR }} aria-hidden="true" />
                  <div className="manager-row-label">
                    <div>Your squad (est.)</div>
                    <div className="hint">{squad?.name}</div>
                  </div>
                  <div className="manager-row-points">{squadEstimate !== null ? squadEstimate : '—'}</div>
                </div>
              )}

              {managers.map((manager) => {
                const color = colorByEntryId.get(manager.entryId);
                const points = managerLivePoints?.get(manager.entryId)?.points;
                const expanded = expandedEntryId === manager.entryId;
                return (
                  <div key={manager.entryId}>
                    <div className="manager-row">
                      {color && (
                        <span className="gameweek-chart-swatch" style={{ background: color }} aria-hidden="true" />
                      )}
                      <div className="manager-row-label">
                        <div>{manager.teamName}</div>
                        <div className="hint">{manager.managerName}</div>
                      </div>
                      <div className="manager-row-points">{points !== undefined ? points : '—'}</div>
                      <button
                        type="button"
                        className="ghost small"
                        aria-expanded={expanded}
                        onClick={() => setExpandedEntryId(expanded ? null : manager.entryId)}
                      >
                        {expanded ? 'Hide comparison' : 'Compare'}
                      </button>
                      <button type="button" className="ghost small" onClick={() => onRemoveManager(manager.entryId)}>
                        Remove
                      </button>
                    </div>
                    {expanded && <RivalDifferentials entryId={manager.entryId} squad={squad} />}
                  </div>
                );
              })}
            </div>
          )}

          {overflowCount > 0 && (
            <p className="hint mt-md">
              Only the first {managerPalette.length} tracked managers are charted below — {overflowCount} more{' '}
              {overflowCount === 1 ? 'is' : 'are'} tracked but not plotted.
            </p>
          )}

          {chartSeries.some((s) => s.points.length > 0) ? (
            <div className="mt-md">
              <GameweekChart series={chartSeries} />
            </div>
          ) : (squad || managers.length > 0) ? (
            <p className="hint mt-md">No live points yet this gameweek.</p>
          ) : null}

          <div className="row row--flush mt-md">
            <button
              type="button"
              className="ghost small"
              onClick={refresh}
              disabled={refreshing}
              aria-busy={refreshing}
            >
              {refreshing && <span className="spinner" aria-hidden="true" />}
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </>
      )}

      {error && <p className="hint text-bad">{error}</p>}
    </section>
  );
}
